/**
 * Runtime backstop for legacy leave writers that were revoked by the private-plane cutover.
 *
 * The database REVOKEs remain the primary authority. Every Supabase client constructed by
 * Statura source must pass through this guard; once guarded, dynamic names and rpc methods
 * passed through callbacks, aliases, bind, object methods, or nested closures remain blocked.
 */

export const HELD_LEGACY_LEAVE_WRITER_RPC_NAMES = [
  'apply_provider_closing_leave_import_controlled',
  'apply_provider_roster_import_controlled',
  'approve_leave_request_controlled',
  'approve_leave_request_core_controlled',
  'approve_leave_request_with_coverage_controlled',
  'approve_leave_request_with_shift_release_controlled',
  'bulk_reassign_worker_controlled',
  'cancel_leave_request_controlled',
  'correct_provider_imported_leave_windows_controlled',
  'import_provider_leave_request_controlled',
  'preview_provider_roster_import_manifest_controlled',
  'reject_leave_request_controlled',
  'replace_staff_for_period_controlled',
  'submit_leave_request_controlled',
  'submit_leave_request_idempotent_controlled',
  'withdraw_leave_request_controlled',
] as const;

export type HeldLegacyLeaveWriterRpcName = (typeof HELD_LEGACY_LEAVE_WRITER_RPC_NAMES)[number];

/**
 * Names REVOKEd by migration 20271031000010 that the CLIENT guard deliberately
 * does not hold, each with its accountable reason. The SQL REVOKEs remain the
 * authority at apply time; these exemptions exist because holding the name at
 * the client would break a live production surface before the private path
 * can replace it. The lockstep test asserts guard + exemptions == migration
 * inventory, so an exemption can never appear silently.
 *
 * Provider roster/private-leave import now routes through service-role-only
 * *_private_server_controlled wrappers (20271036000023); the legacy names stay
 * held with no client exemptions.
 */
export const CLIENT_GUARD_EXEMPT_REVOKED_RPC_NAMES: Readonly<Record<string, string>> = {};

const HELD_NAMES = new Set<string>(HELD_LEGACY_LEAVE_WRITER_RPC_NAMES);
const CANONICAL_RPC_IDENTIFIER = /^[a-z_][a-z0-9_]*$/;
const GUARDED_RPC_TARGETS = new WeakSet<object>();
const GUARDED_RPC_PROTOTYPES = new WeakSet<object>();

type RpcCapableClient = {
  rpc: (name: unknown, ...args: unknown[]) => unknown;
};

export class HeldLegacyLeaveWriterRpcError extends Error {
  readonly code = 'leave_revoked_writer_rpc_held';

  constructor() {
    super('This legacy leave write path is held by the private leave cutover.');
    this.name = 'HeldLegacyLeaveWriterRpcError';
  }
}

export function isHeldLegacyLeaveWriterRpcName(
  name: unknown,
): name is HeldLegacyLeaveWriterRpcName {
  return typeof name === 'string' && HELD_NAMES.has(name);
}

export function assertLegacyLeaveWriterRpcAllowed(name: unknown): void {
  if (typeof name !== 'string' || !CANONICAL_RPC_IDENTIFIER.test(name)) {
    throw new TypeError('leave_rpc_name_must_be_canonical_identifier');
  }
  if (isHeldLegacyLeaveWriterRpcName(name)) {
    throw new HeldLegacyLeaveWriterRpcError();
  }
}

function guardRpcPrototype(target: object): void {
  let owner = Object.getPrototypeOf(target) as object | null;
  while (owner && !Object.prototype.hasOwnProperty.call(owner, 'rpc')) {
    owner = Object.getPrototypeOf(owner) as object | null;
  }
  if (!owner || GUARDED_RPC_PROTOTYPES.has(owner)) return;

  const descriptor = Object.getOwnPropertyDescriptor(owner, 'rpc');
  if (!descriptor || typeof descriptor.value !== 'function' || !descriptor.configurable) return;

  const originalPrototypeRpc = descriptor.value as RpcCapableClient['rpc'];
  Object.defineProperty(owner, 'rpc', {
    ...descriptor,
    value: function (this: unknown, name: unknown, ...args: unknown[]) {
      assertLegacyLeaveWriterRpcAllowed(name);
      return Reflect.apply(originalPrototypeRpc, this, [name, ...args]);
    },
  });
  GUARDED_RPC_PROTOTYPES.add(owner);
}

function guardRpcTarget(target: object): void {
  if (GUARDED_RPC_TARGETS.has(target)) return;
  const rpcTarget = target as RpcCapableClient;
  if (typeof rpcTarget.rpc !== 'function') {
    throw new TypeError('leave_revoked_writer_rpc_guard_requires_rpc_client');
  }

  const originalRpc = rpcTarget.rpc;
  guardRpcPrototype(target);
  Object.defineProperty(rpcTarget, 'rpc', {
    configurable: false,
    enumerable: false,
    writable: false,
    value: function (this: unknown, name: unknown, ...args: unknown[]) {
      assertLegacyLeaveWriterRpcAllowed(name);
      return Reflect.apply(originalRpc, target, [name, ...args]);
    },
  });
  GUARDED_RPC_TARGETS.add(target);
}

/**
 * Mutates the public Supabase RPC entrypoint and the embedded PostgREST RPC
 * entrypoint while preserving the original client identity. Patching the
 * PostgREST prototype also contains clients returned by `client.schema()`.
 * WeakSet identity tracking makes repeated wrapping idempotent.
 */
function freezeEmbeddedRestProperty(client: object, embeddedRest: object): void {
  const descriptor = Object.getOwnPropertyDescriptor(client, 'rest');
  Object.defineProperty(client, 'rest', {
    configurable: false,
    enumerable: descriptor?.enumerable ?? true,
    writable: false,
    value: embeddedRest,
  });
}

export function guardHeldLeaveWriterRpcClient<T extends object>(client: T): T {
  if (GUARDED_RPC_TARGETS.has(client)) return client;

  const embeddedRest = Reflect.get(client, 'rest');
  if (
    embeddedRest !== null &&
    typeof embeddedRest === 'object' &&
    typeof Reflect.get(embeddedRest, 'rpc') === 'function'
  ) {
    guardRpcTarget(embeddedRest);
    freezeEmbeddedRestProperty(client, embeddedRest);
  }
  guardRpcTarget(client);
  return client;
}
